import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Close,
  NavigateBefore,
  NavigateNext,
  Image as ImageIcon,
  PlayArrow,
  RotateRight,
  Refresh
} from '@mui/icons-material';
import { clipAudioApi, aiModelsApi } from '../../../services/api';

function ImagesAnglesDisplay({ imagesAngles, projectId, onProjectUpdate }) {
  // Modal state for image preview
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState('');
  const [modalSceneId, setModalSceneId] = useState(null);
  const [modalSelectedImageIndex, setModalSelectedImageIndex] = useState(null);
  const [modalAngleIndex, setModalAngleIndex] = useState(null);
  const [isViewingAngle, setIsViewingAngle] = useState(false);

  // Dialog state for angle generation
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generatingSceneId, setGeneratingSceneId] = useState(null);
  const [generatingImageIndex, setGeneratingImageIndex] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [nbVariations, setNbVariations] = useState(1);
  const [selectedAngles, setSelectedAngles] = useState({
    top: false,
    top_left: false,
    top_right: false,
    right: false,
    left: false,
    bottom: false,
    bottom_left: false,
    bottom_right: false,
    front: false,
    back: false
  });

  // Dialog state for regeneration
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [regeneratingImageUrl, setRegeneratingImageUrl] = useState(null);
  const [regeneratingSceneId, setRegeneratingSceneId] = useState(null);
  const [regeneratingSelectedImageIndex, setRegeneratingSelectedImageIndex] = useState(null);
  const [regeneratingAngleIndex, setRegeneratingAngleIndex] = useState(null);
  const [isViewingAngleForRegeneration, setIsViewingAngleForRegeneration] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regeneratePrompt, setRegeneratePrompt] = useState('');
  const [regenerateModel, setRegenerateModel] = useState('');
  const [imageGenerationModels, setImageGenerationModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  // Fetch AI models for image generation
  useEffect(() => {
    const fetchModels = async () => {
      try {
        setModelsLoading(true);
        const models = await aiModelsApi.getAIModels('image_generation_models');
        const modelsArray = Array.isArray(models) ? models : [];
        setImageGenerationModels(modelsArray);
        if (modelsArray.length > 0) {
          setRegenerateModel(modelsArray[0].id);
        }
      } catch (err) {
        console.error('Error fetching AI models:', err);
        setImageGenerationModels([]);
      } finally {
        setModelsLoading(false);
      }
    };

    fetchModels();
  }, []);

  const handleOpenImageModal = (sceneId, selectedImageIndex, angleIndex = null, imageUrl) => {
    setModalImageUrl(imageUrl);
    setModalSceneId(sceneId);
    setModalSelectedImageIndex(selectedImageIndex);
    setModalAngleIndex(angleIndex);
    setIsViewingAngle(angleIndex !== null);
    setModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setModalOpen(false);
    setModalImageUrl('');
    setModalSceneId(null);
    setModalSelectedImageIndex(null);
    setModalAngleIndex(null);
    setIsViewingAngle(false);
  };

  const handleNavigateImage = (direction) => {
    if (!imagesAngles || modalSceneId === null || modalSelectedImageIndex === null) return;

    const sceneData = imagesAngles.find(item => item.scene_id === modalSceneId);
    if (!sceneData || !sceneData.selected_images) return;

    const currentSelectedImage = sceneData.selected_images[modalSelectedImageIndex];
    if (!currentSelectedImage) return;

    if (isViewingAngle && currentSelectedImage.images_angles && currentSelectedImage.images_angles.length > 0) {
      // Navigating through angles
      const anglesData = currentSelectedImage.images_angles.map(angle => {
        if (typeof angle === 'string') {
          return { img_angle_url: angle, selected: false };
        }
        return angle;
      });
      const newAngleIndex = direction === 'next' 
        ? (modalAngleIndex + 1) % anglesData.length
        : (modalAngleIndex - 1 + anglesData.length) % anglesData.length;
      
      setModalAngleIndex(newAngleIndex);
      const angleObj = anglesData[newAngleIndex];
      const angleUrl = angleObj.img_angle_url || angleObj;
      setModalImageUrl(angleUrl);
    } else {
      // Navigating through selected images
      const newSelectedImageIndex = direction === 'next'
        ? (modalSelectedImageIndex + 1) % sceneData.selected_images.length
        : (modalSelectedImageIndex - 1 + sceneData.selected_images.length) % sceneData.selected_images.length;
      
      const newSelectedImage = sceneData.selected_images[newSelectedImageIndex];
      setModalSelectedImageIndex(newSelectedImageIndex);
      setModalImageUrl(newSelectedImage.url);
      setIsViewingAngle(false);
      setModalAngleIndex(null);
    }
  };

  const handleOpenGenerateDialog = (sceneId, imageIndex) => {
    setGeneratingSceneId(sceneId);
    setGeneratingImageIndex(imageIndex);
    setNbVariations(1);
    setSelectedAngles({
      top: false,
      top_left: false,
      top_right: false,
      right: false,
      left: false,
      bottom: false,
      bottom_left: false,
      bottom_right: false,
      front: false,
      back: false
    });
    setGenerateDialogOpen(true);
  };

  const handleCloseGenerateDialog = () => {
    setGenerateDialogOpen(false);
    setGeneratingSceneId(null);
    setGeneratingImageIndex(null);
  };

  const handleToggleAngle = (angleType) => {
    setSelectedAngles(prev => ({
      ...prev,
      [angleType]: !prev[angleType]
    }));
  };

  const handleGenerateAngles = async () => {
    if (!projectId || generatingSceneId === null || generatingImageIndex === null) return;

    // Check if at least one angle is selected
    const hasSelectedAngle = Object.values(selectedAngles).some(val => val);
    if (!hasSelectedAngle) {
      alert('Veuillez sélectionner au moins un angle à générer.');
      return;
    }

    setIsGenerating(true);
    try {
      const selectedAngleTypes = Object.entries(selectedAngles)
        .filter(([_, selected]) => selected)
        .map(([angleType, _]) => angleType);

      const stepData = {
        scene_id: generatingSceneId,
        image_index: generatingImageIndex,
        nb_variations: nbVariations,
        angles: selectedAngleTypes
      };

      const result = await clipAudioApi.executeStep(projectId, 4, stepData);
      console.log('Angles generation successful:', result);

      if (onProjectUpdate) {
        await onProjectUpdate();
      }

      handleCloseGenerateDialog();
    } catch (error) {
      console.error('Error generating angles:', error);
      alert('Erreur lors de la génération des angles. Veuillez réessayer.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleOpenRegenerateDialog = () => {
    // Only allow regeneration for angles on this screen
    if (modalSceneId === null || modalSelectedImageIndex === null || modalAngleIndex === null) return;

    setRegeneratingSceneId(modalSceneId);
    setRegeneratingSelectedImageIndex(modalSelectedImageIndex);
    setRegeneratingAngleIndex(modalAngleIndex);
    setIsViewingAngleForRegeneration(true);
    setRegeneratePrompt('');
    if (imageGenerationModels.length > 0 && !regenerateModel) {
      setRegenerateModel(imageGenerationModels[0].id);
    }
    setRegenerateDialogOpen(true);
  };

  const handleCloseRegenerateDialog = () => {
    setRegenerateDialogOpen(false);
    setRegeneratingImageUrl(null);
    setRegeneratingSceneId(null);
    setRegeneratingSelectedImageIndex(null);
    setRegeneratingAngleIndex(null);
    setIsViewingAngleForRegeneration(false);
    setRegeneratePrompt('');
  };

  const handleRegenerate = async () => {
    if (!projectId || regeneratingSceneId === null || regeneratingSelectedImageIndex === null || !regeneratePrompt.trim()) {
      alert('Veuillez remplir le prompt de régénération.');
      return;
    }

    if (!regenerateModel) {
      alert('Veuillez sélectionner un modèle.');
      return;
    }

    setIsRegenerating(true);
    try {
      // Always use the dedicated angle regeneration endpoint on this screen
      const result = await clipAudioApi.updateImageAngle(
        projectId,
        regeneratingSceneId,
        regeneratingSelectedImageIndex,
        regeneratePrompt,
        regenerateModel
      );
      console.log('Image regeneration successful:', result);

      if (onProjectUpdate) {
        await onProjectUpdate();
      }

      handleCloseRegenerateDialog();
      handleCloseImageModal(); // Also close the carousel modal
    } catch (error) {
      console.error('Error regenerating image:', error);
      alert('Erreur lors de la régénération de l\'image. Veuillez réessayer.');
    } finally {
      setIsRegenerating(false);
    }
  };

  if (!imagesAngles || imagesAngles.length === 0) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        <Typography variant="body1">
          Aucune image d'angle générée pour le moment. Les images d'angle seront affichées ici une fois générées.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      {imagesAngles.map((sceneData, sceneIndex) => {
        const scene = sceneData.scene || {};
        const selectedImages = sceneData.selected_images || [];

        return (
          <Card 
            key={sceneData.scene_id || sceneIndex}
            sx={{ 
              mb: 3, 
              borderRadius: 3, 
              bgcolor: '#FFFFFF',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              {/* Scene Header */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`Scène ${scene.number || sceneData.scene_id || sceneIndex + 1}`}
                    color="primary"
                    variant="filled"
                    sx={{ fontWeight: 'bold' }}
                  />
                  <Typography variant="h6" sx={{ color: '#2C3E50', fontWeight: 'bold' }}>
                    {scene.title || 'Sans titre'}
                  </Typography>
                </Box>
                {sceneData.lyric_line && (
                  <Typography variant="body2" sx={{ color: '#E65100', fontStyle: 'italic', mb: 1 }}>
                    🎵 {sceneData.lyric_line}
                  </Typography>
                )}
                {scene.one_line && (
                  <Typography variant="body2" sx={{ color: '#7F8C8D', mb: 2 }}>
                    {scene.one_line}
                  </Typography>
                )}
              </Box>

              {/* Selected Images and their Angles */}
              {selectedImages.length === 0 ? (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  Aucune image sélectionnée pour cette scène.
                </Alert>
              ) : (
                selectedImages.map((selectedImage, selectedImageIndex) => {
                  const angles = selectedImage.images_angles || [];
                  // Handle both array of strings (legacy) and array of objects with img_angle_url
                  const anglesData = angles.map(angle => {
                    if (typeof angle === 'string') {
                      return { img_angle_url: angle, selected: false };
                    }
                    return angle; // Already an object with img_angle_url
                  });
                  const hasAngles = anglesData.length > 0;

                  return (
                    <Box 
                      key={selectedImageIndex}
                      sx={{ 
                        mb: 3,
                        p: 2,
                        bgcolor: '#F8F9FA',
                        borderRadius: 2,
                        border: '1px solid #E1E8ED'
                      }}
                    >
                      {/* Selected Image Header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2C3E50' }}>
                            Image sélectionnée #{selectedImageIndex + 1}
                          </Typography>
                        <Chip
                          label={hasAngles ? `${anglesData.length} angle${anglesData.length > 1 ? 's' : ''} généré${anglesData.length > 1 ? 's' : ''}` : 'Aucun angle'}
                          color={hasAngles ? 'success' : 'default'}
                          size="small"
                        />
                        </Box>
                        {projectId && (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={isGenerating && generatingSceneId === sceneData.scene_id && generatingImageIndex === selectedImageIndex ? <CircularProgress size={16} /> : <RotateRight />}
                            onClick={() => handleOpenGenerateDialog(sceneData.scene_id, selectedImageIndex)}
                            disabled={isGenerating}
                            sx={{
                              background: 'linear-gradient(135deg, #8E44AD 0%, #9B59B6 100%)',
                              borderRadius: '20px',
                              px: 2,
                              py: 0.5,
                              fontSize: '0.8rem',
                              fontWeight: 'bold',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #7D3C98 0%, #8E44AD 100%)',
                                transform: 'translateY(-1px)'
                              },
                              '&:disabled': {
                                background: '#BDBDBD',
                                transform: 'none'
                              }
                            }}
                          >
                            {isGenerating && generatingSceneId === sceneData.scene_id && generatingImageIndex === selectedImageIndex ? 'Génération...' : 'Générer angles'}
                          </Button>
                        )}
                      </Box>

                      <Grid container spacing={2}>
                        {/* Selected Image */}
                        <Grid item xs={12} sm={6} md={4}>
                          <Paper
                            sx={{
                              position: 'relative',
                              borderRadius: 2,
                              overflow: 'hidden',
                              border: '3px solid #3498DB',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'scale(1.02)',
                                boxShadow: '0 8px 24px rgba(52, 152, 219, 0.3)'
                              }
                            }}
                            onClick={() => handleOpenImageModal(sceneData.scene_id, selectedImageIndex, null, selectedImage.url)}
                          >
                            <Box sx={{ position: 'relative', width: '100%', paddingTop: '75%', bgcolor: '#F5F5F5' }}>
                              <img
                                src={selectedImage.url}
                                alt={`Selected Image ${selectedImageIndex + 1}`}
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  display: 'block'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = 'flex';
                                  }
                                }}
                              />
                              <Box sx={{
                                display: 'none',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: '#F5F5F5',
                                color: '#999'
                              }}>
                                <Typography variant="caption">Image non disponible</Typography>
                              </Box>
                            </Box>

                            {/* Label */}
                            <Box sx={{ p: 1, bgcolor: '#3498DB', textAlign: 'center' }}>
                              <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>
                                Image principale
                              </Typography>
                            </Box>

                            {/* Expand Icon */}
                            <IconButton
                              sx={{
                                position: 'absolute',
                                bottom: 40,
                                right: 8,
                                bgcolor: 'rgba(255, 255, 255, 0.9)',
                                '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                              }}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenImageModal(sceneData.scene_id, selectedImageIndex, null, selectedImage.url);
                              }}
                            >
                              <PlayArrow fontSize="small" />
                            </IconButton>
                          </Paper>
                        </Grid>

                        {/* Angle Images */}
                        {hasAngles ? (
                          anglesData.map((angleObj, angleIndex) => {
                            const angleUrl = angleObj.img_angle_url || angleObj; // Support both formats
                            return (
                            <Grid item xs={12} sm={6} md={4} key={angleIndex}>
                              <Paper
                                sx={{
                                  position: 'relative',
                                  borderRadius: 2,
                                  overflow: 'hidden',
                                  border: '2px solid #27AE60',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'scale(1.02)',
                                    boxShadow: '0 8px 24px rgba(39, 174, 96, 0.3)'
                                  }
                                }}
                                onClick={() => handleOpenImageModal(sceneData.scene_id, selectedImageIndex, angleIndex, angleUrl)}
                              >
                                <Box sx={{ position: 'relative', width: '100%', paddingTop: '75%', bgcolor: '#F5F5F5' }}>
                                  <img
                                    src={angleUrl}
                                    alt={`Angle ${angleIndex + 1}`}
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      display: 'block'
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      if (e.target.nextSibling) {
                                        e.target.nextSibling.style.display = 'flex';
                                      }
                                    }}
                                  />
                                  <Box sx={{
                                    display: 'none',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: '#F5F5F5',
                                    color: '#999'
                                  }}>
                                    <Typography variant="caption">Image non disponible</Typography>
                                  </Box>
                                </Box>

                                {/* Label */}
                                <Box sx={{ p: 1, bgcolor: '#27AE60', textAlign: 'center' }}>
                                  <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>
                                    Angle {angleIndex + 1}
                                  </Typography>
                                </Box>

                                {/* Expand Icon */}
                                <IconButton
                                  sx={{
                                    position: 'absolute',
                                    bottom: 40,
                                    right: 8,
                                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                                  }}
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenImageModal(sceneData.scene_id, selectedImageIndex, angleIndex, angleUrl);
                                  }}
                                >
                                  <PlayArrow fontSize="small" />
                                </IconButton>
                              </Paper>
                            </Grid>
                            );
                          })
                        ) : (
                          <Grid item xs={12} sm={6} md={8}>
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                              <Typography variant="body2">
                                Aucun angle généré pour cette image. Les angles seront affichés ici une fois générés.
                              </Typography>
                            </Alert>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  );
                })
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Image Preview Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseImageModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: '#FFFFFF'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pb: 1
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {isViewingAngle ? 'Angle' : 'Image'} - Scène {modalSceneId}
              {modalSelectedImageIndex !== null && ` (Image ${modalSelectedImageIndex + 1})`}
              {isViewingAngle && modalAngleIndex !== null && ` - Angle ${modalAngleIndex + 1}`}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseImageModal} sx={{ color: '#7F8C8D' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ textAlign: 'center', p: 2 }}>
          {modalImageUrl && (
            <Box sx={{ position: 'relative', maxWidth: '100%', maxHeight: '70vh' }}>
              <img
                src={modalImageUrl}
                alt="Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: 8,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                }}
              />
              
              {/* Navigation Arrows */}
              {(() => {
                if (modalSceneId === null || modalSelectedImageIndex === null) return null;

                const sceneData = imagesAngles.find(item => item.scene_id === modalSceneId);
                if (!sceneData || !sceneData.selected_images) return null;

                const currentSelectedImage = sceneData.selected_images[modalSelectedImageIndex];
                if (!currentSelectedImage) return null;

                // Convert angles to normalized format for counting
                const anglesData = currentSelectedImage.images_angles ? currentSelectedImage.images_angles.map(angle => {
                  if (typeof angle === 'string') {
                    return { img_angle_url: angle, selected: false };
                  }
                  return angle;
                }) : [];

                if (isViewingAngle && anglesData.length > 1) {
                  // Show navigation for angles
                  return (
                    <>
                      <IconButton
                        onClick={() => handleNavigateImage('prev')}
                        sx={{
                          position: 'absolute',
                          left: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          bgcolor: 'rgba(255, 255, 255, 0.9)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                        }}
                      >
                        <NavigateBefore />
                      </IconButton>
                      <IconButton
                        onClick={() => handleNavigateImage('next')}
                        sx={{
                          position: 'absolute',
                          right: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          bgcolor: 'rgba(255, 255, 255, 0.9)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                        }}
                      >
                        <NavigateNext />
                      </IconButton>
                      <Typography variant="body2" sx={{ mt: 1.5, color: '#7F8C8D', fontWeight: 'bold' }}>
                        Angle {modalAngleIndex + 1} / {anglesData.length}
                      </Typography>
                    </>
                  );
                } else if (!isViewingAngle && sceneData.selected_images.length > 1) {
                  // Show navigation for selected images
                  return (
                    <>
                      <IconButton
                        onClick={() => handleNavigateImage('prev')}
                        sx={{
                          position: 'absolute',
                          left: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          bgcolor: 'rgba(255, 255, 255, 0.9)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                        }}
                      >
                        <NavigateBefore />
                      </IconButton>
                      <IconButton
                        onClick={() => handleNavigateImage('next')}
                        sx={{
                          position: 'absolute',
                          right: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          bgcolor: 'rgba(255, 255, 255, 0.9)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                        }}
                      >
                        <NavigateNext />
                      </IconButton>
                      <Typography variant="body2" sx={{ mt: 1.5, color: '#7F8C8D', fontWeight: 'bold' }}>
                        Image {modalSelectedImageIndex + 1} / {sceneData.selected_images.length}
                      </Typography>
                    </>
                  );
                }
                return null;
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'center', gap: 2 }}>
          {projectId && isViewingAngle && (
            <Button
              variant="contained"
              startIcon={isRegenerating ? <CircularProgress size={16} /> : <Refresh />}
              onClick={handleOpenRegenerateDialog}
              disabled={isRegenerating}
              sx={{
                background: 'linear-gradient(135deg, #E74C3C 0%, #C0392B 100%)',
                borderRadius: '20px',
                px: 3,
                py: 1,
                fontWeight: 'bold',
                '&:hover': {
                  background: 'linear-gradient(135deg, #C0392B 0%, #A93226 100%)',
                  transform: 'translateY(-1px)'
                },
                '&:disabled': {
                  background: '#BDBDBD',
                  transform: 'none'
                }
              }}
            >
              Regenerer
            </Button>
          )}
          <Button
            onClick={handleCloseImageModal}
            variant="outlined"
            sx={{
              borderRadius: '20px',
              px: 3,
              borderColor: '#95A5A6',
              color: '#7F8C8D',
              '&:hover': {
                borderColor: '#7F8C8D',
                backgroundColor: '#F8F9FA'
              }
            }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Angles Dialog */}
      <Dialog
        open={generateDialogOpen}
        onClose={handleCloseGenerateDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: '#FFFFFF'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2,
          background: 'linear-gradient(135deg, #8E44AD 0%, #9B59B6 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            🎨 Générer des angles d'image
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, pt: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Number of Variations */}
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="variations-label">Nombre de variations par angle</InputLabel>
              <Select
                labelId="variations-label"
                value={nbVariations}
                label="Nombre de variations par angle"
                onChange={(e) => setNbVariations(e.target.value)}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                  <MenuItem key={num} value={num}>
                    {num} variation{num > 1 ? 's' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Angle Types Selection */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#2C3E50' }}>
                Sélectionnez les angles à générer :
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                {/* Row 1: Top row - left, middle, right */}
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedAngles['top_left']}
                        onChange={() => handleToggleAngle('top_left')}
                        sx={{
                          color: '#8E44AD',
                          '&.Mui-checked': { color: '#8E44AD' }
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">↖️</Typography>
                        <Typography variant="body2">Coin haut gauche</Typography>
                      </Box>
                    }
                    sx={{
                      border: selectedAngles['top_left'] ? '2px solid #8E44AD' : '2px solid #E1E8ED',
                      borderRadius: 2,
                      p: 1,
                      m: 0,
                      width: '100%',
                      bgcolor: selectedAngles['top_left'] ? '#F4E6FF' : 'transparent',
                      transition: 'all 0.3s ease',
                      '&:hover': { bgcolor: selectedAngles['top_left'] ? '#E8D5FF' : '#F8F9FA' }
                    }}
                  />
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedAngles['top']}
                        onChange={() => handleToggleAngle('top')}
                        sx={{ color: '#8E44AD', '&.Mui-checked': { color: '#8E44AD' } }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">⬆️</Typography>
                        <Typography variant="body2">Vue de dessus</Typography>
                      </Box>
                    }
                    sx={{
                      border: selectedAngles['top'] ? '2px solid #8E44AD' : '2px solid #E1E8ED',
                      borderRadius: 2,
                      p: 1,
                      m: 0,
                      width: '100%',
                      bgcolor: selectedAngles['top'] ? '#F4E6FF' : 'transparent',
                      transition: 'all 0.3s ease',
                      '&:hover': { bgcolor: selectedAngles['top'] ? '#E8D5FF' : '#F8F9FA' }
                    }}
                  />
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedAngles['top_right']}
                        onChange={() => handleToggleAngle('top_right')}
                        sx={{ color: '#8E44AD', '&.Mui-checked': { color: '#8E44AD' } }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">↗️</Typography>
                        <Typography variant="body2">Coin haut droite</Typography>
                      </Box>
                    }
                    sx={{
                      border: selectedAngles['top_right'] ? '2px solid #8E44AD' : '2px solid #E1E8ED',
                      borderRadius: 2,
                      p: 1,
                      m: 0,
                      width: '100%',
                      bgcolor: selectedAngles['top_right'] ? '#F4E6FF' : 'transparent',
                      transition: 'all 0.3s ease',
                      '&:hover': { bgcolor: selectedAngles['top_right'] ? '#E8D5FF' : '#F8F9FA' }
                    }}
                  />
                </Box>

                {/* Row 2: Middle row - left, middle(front), right */}
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedAngles['left']}
                        onChange={() => handleToggleAngle('left')}
                        sx={{ color: '#8E44AD', '&.Mui-checked': { color: '#8E44AD' } }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">⬅️</Typography>
                        <Typography variant="body2">Vue de gauche</Typography>
                      </Box>
                    }
                    sx={{
                      border: selectedAngles['left'] ? '2px solid #8E44AD' : '2px solid #E1E8ED',
                      borderRadius: 2,
                      p: 1,
                      m: 0,
                      width: '100%',
                      bgcolor: selectedAngles['left'] ? '#F4E6FF' : 'transparent',
                      transition: 'all 0.3s ease',
                      '&:hover': { bgcolor: selectedAngles['left'] ? '#E8D5FF' : '#F8F9FA' }
                    }}
                  />
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedAngles['front']}
                        onChange={() => handleToggleAngle('front')}
                        sx={{ color: '#8E44AD', '&.Mui-checked': { color: '#8E44AD' } }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">👁️</Typography>
                        <Typography variant="body2">Vue de face</Typography>
                      </Box>
                    }
                    sx={{
                      border: selectedAngles['front'] ? '2px solid #8E44AD' : '2px solid #E1E8ED',
                      borderRadius: 2,
                      p: 1,
                      m: 0,
                      width: '100%',
                      bgcolor: selectedAngles['front'] ? '#F4E6FF' : 'transparent',
                      transition: 'all 0.3s ease',
                      '&:hover': { bgcolor: selectedAngles['front'] ? '#E8D5FF' : '#F8F9FA' }
                    }}
                  />
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedAngles['right']}
                        onChange={() => handleToggleAngle('right')}
                        sx={{ color: '#8E44AD', '&.Mui-checked': { color: '#8E44AD' } }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">➡️</Typography>
                        <Typography variant="body2">Vue de droite</Typography>
                      </Box>
                    }
                    sx={{
                      border: selectedAngles['right'] ? '2px solid #8E44AD' : '2px solid #E1E8ED',
                      borderRadius: 2,
                      p: 1,
                      m: 0,
                      width: '100%',
                      bgcolor: selectedAngles['right'] ? '#F4E6FF' : 'transparent',
                      transition: 'all 0.3s ease',
                      '&:hover': { bgcolor: selectedAngles['right'] ? '#E8D5FF' : '#F8F9FA' }
                    }}
                  />
                </Box>

                {/* Row 3: Bottom row - left, middle, right */}
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedAngles['bottom_left']}
                        onChange={() => handleToggleAngle('bottom_left')}
                        sx={{ color: '#8E44AD', '&.Mui-checked': { color: '#8E44AD' } }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">↙️</Typography>
                        <Typography variant="body2">Coin bas gauche</Typography>
                      </Box>
                    }
                    sx={{
                      border: selectedAngles['bottom_left'] ? '2px solid #8E44AD' : '2px solid #E1E8ED',
                      borderRadius: 2,
                      p: 1,
                      m: 0,
                      width: '100%',
                      bgcolor: selectedAngles['bottom_left'] ? '#F4E6FF' : 'transparent',
                      transition: 'all 0.3s ease',
                      '&:hover': { bgcolor: selectedAngles['bottom_left'] ? '#E8D5FF' : '#F8F9FA' }
                    }}
                  />
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedAngles['bottom']}
                        onChange={() => handleToggleAngle('bottom')}
                        sx={{ color: '#8E44AD', '&.Mui-checked': { color: '#8E44AD' } }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">⬇️</Typography>
                        <Typography variant="body2">Vue de dessous</Typography>
                      </Box>
                    }
                    sx={{
                      border: selectedAngles['bottom'] ? '2px solid #8E44AD' : '2px solid #E1E8ED',
                      borderRadius: 2,
                      p: 1,
                      m: 0,
                      width: '100%',
                      bgcolor: selectedAngles['bottom'] ? '#F4E6FF' : 'transparent',
                      transition: 'all 0.3s ease',
                      '&:hover': { bgcolor: selectedAngles['bottom'] ? '#E8D5FF' : '#F8F9FA' }
                    }}
                  />
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedAngles['bottom_right']}
                        onChange={() => handleToggleAngle('bottom_right')}
                        sx={{ color: '#8E44AD', '&.Mui-checked': { color: '#8E44AD' } }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">↘️</Typography>
                        <Typography variant="body2">Coin bas droite</Typography>
                      </Box>
                    }
                    sx={{
                      border: selectedAngles['bottom_right'] ? '2px solid #8E44AD' : '2px solid #E1E8ED',
                      borderRadius: 2,
                      p: 1,
                      m: 0,
                      width: '100%',
                      bgcolor: selectedAngles['bottom_right'] ? '#F4E6FF' : 'transparent',
                      transition: 'all 0.3s ease',
                      '&:hover': { bgcolor: selectedAngles['bottom_right'] ? '#E8D5FF' : '#F8F9FA' }
                    }}
                  />
                </Box>

                {/* Row 4: Back centered */}
                <Box />
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedAngles['back']}
                        onChange={() => handleToggleAngle('back')}
                        sx={{ color: '#8E44AD', '&.Mui-checked': { color: '#8E44AD' } }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">🔙</Typography>
                        <Typography variant="body2">Vue de dos</Typography>
                      </Box>
                    }
                    sx={{
                      border: selectedAngles['back'] ? '2px solid #8E44AD' : '2px solid #E1E8ED',
                      borderRadius: 2,
                      p: 1,
                      m: 0,
                      width: '100%',
                      bgcolor: selectedAngles['back'] ? '#F4E6FF' : 'transparent',
                      transition: 'all 0.3s ease',
                      '&:hover': { bgcolor: selectedAngles['back'] ? '#E8D5FF' : '#F8F9FA' }
                    }}
                  />
                </Box>
                <Box />
              </Box>
            </Box>

            {/* Info Alert */}
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Information :</strong> Vous devez sélectionner au moins un angle. Pour chaque angle sélectionné, {nbVariations} variation{nbVariations > 1 ? 's' : ''} {nbVariations > 1 ? 'seront' : 'sera'} générée{nbVariations > 1 ? 's' : ''}.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseGenerateDialog}
            variant="outlined"
            disabled={isGenerating}
            sx={{
              borderRadius: '20px',
              px: 3,
              borderColor: '#95A5A6',
              color: '#7F8C8D',
              '&:hover': {
                borderColor: '#7F8C8D',
                backgroundColor: '#F8F9FA'
              }
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleGenerateAngles}
            variant="contained"
            disabled={isGenerating || !Object.values(selectedAngles).some(val => val)}
            startIcon={isGenerating ? <CircularProgress size={16} /> : <RotateRight />}
            sx={{
              background: 'linear-gradient(135deg, #8E44AD 0%, #9B59B6 100%)',
              borderRadius: '20px',
              px: 3,
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #7D3C98 0%, #8E44AD 100%)',
                transform: 'translateY(-1px)'
              },
              '&:disabled': {
                background: '#BDBDBD',
                transform: 'none'
              }
            }}
          >
            {isGenerating ? 'Génération en cours...' : 'Générer les angles'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regenerate Image Dialog */}
      <Dialog
        open={regenerateDialogOpen}
        onClose={handleCloseRegenerateDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: '#FFFFFF'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2,
          background: 'linear-gradient(135deg, #E74C3C 0%, #C0392B 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            🔄 Régénérer l'image
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, pt: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Model Selection */}
            <FormControl fullWidth sx={{ mt: 3 }}>
              <InputLabel id="regenerate-model-label">Modèle</InputLabel>
              <Select
                labelId="regenerate-model-label"
                value={regenerateModel}
                label="Modèle"
                onChange={(e) => setRegenerateModel(e.target.value)}
                disabled={modelsLoading || isRegenerating}
              >
                {modelsLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Chargement des modèles...
                  </MenuItem>
                ) : (
                  imageGenerationModels.map((model) => (
                    <MenuItem key={model.id} value={model.id}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {model.name}
                      </Typography>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {/* Prompt Text Field */}
            <TextField
              label="Prompt de régénération"
              placeholder="Décrivez comment vous voulez modifier l'image..."
              multiline
              rows={4}
              value={regeneratePrompt}
              onChange={(e) => setRegeneratePrompt(e.target.value)}
              disabled={isRegenerating}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#E74C3C',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#E74C3C',
                  },
                },
              }}
            />

            {/* Info Alert */}
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Information :</strong> L'image sera régénérée avec le modèle sélectionné et le prompt que vous avez entré. Cette action remplacera l'image actuelle.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseRegenerateDialog}
            variant="outlined"
            disabled={isRegenerating}
            sx={{
              borderRadius: '20px',
              px: 3,
              borderColor: '#95A5A6',
              color: '#7F8C8D',
              '&:hover': {
                borderColor: '#7F8C8D',
                backgroundColor: '#F8F9FA'
              }
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleRegenerate}
            variant="contained"
            disabled={isRegenerating || !regeneratePrompt.trim() || !regenerateModel}
            startIcon={isRegenerating ? <CircularProgress size={16} /> : <Refresh />}
            sx={{
              background: 'linear-gradient(135deg, #E74C3C 0%, #C0392B 100%)',
              borderRadius: '20px',
              px: 3,
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #C0392B 0%, #A93226 100%)',
                transform: 'translateY(-1px)'
              },
              '&:disabled': {
                background: '#BDBDBD',
                transform: 'none'
              }
            }}
          >
            {isRegenerating ? 'Régénération en cours...' : 'Régénérer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ImagesAnglesDisplay;
